import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import { Tooltip } from 'bootstrap';
import { useStore } from 'vuex';
import { AccountAction, AccountGetter } from '@/store/account/account.module';
import { Role } from '@/store/role/role.model';
import { ActivityAction, ActivityGetter } from '@/store/activity/activity.module';
import { Activity } from '@/store/activity/activity.model';
import { HolidayAction, HolidayGetter } from '@/store/holiday/holiday.module';
import { Holiday } from '@/store/holiday/holiday.model';
import ActivityFormComponent from '@/components/activityForm/ActivityForm.vue';
import ActivityOffcanvasComponent from '@/components/activityOffcanvas/ActivityOffcanvas.vue';
import FullCalendar, { CalendarOptions, DateSelectArg, EventClickArg, EventDropArg, EventInput, EventMountArg } from '@fullcalendar/vue3';
import thLocale from '@fullcalendar/core/locales/th';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin, { EventResizeDoneArg } from '@fullcalendar/interaction';

export default defineComponent({
  name: 'ActivityPage',
  components: {
    ActivityFormComponent,
    ActivityOffcanvasComponent,
    FullCalendar,
  },
  setup: () => {
    const store = useStore();
    const role = computed<Role | undefined>(() => store.getters[AccountGetter.SINGLE_ROLE]);
    const isOrs = computed(() => role.value?.name === 'เจ้าหน้าที่ ส.กกภ.');
    const tooltipList = ref<Tooltip[]>([]);
    const activities = computed<Activity[]>(() => store.getters[ActivityGetter.ALL]);
    const holidays = computed<Holiday[]>(() => store.getters[HolidayGetter.ALL]);
    const activityForm = ref<InstanceType<typeof ActivityFormComponent>>();
    const activityOffcanvas = ref<InstanceType<typeof ActivityOffcanvasComponent>>();
    const startDate = ref<Date | null>();
    const endDate = ref<Date | null>();
    const selectedActivityId = ref<number | null>();
    const fullCalendarEvents = computed<EventInput[]>(() => getEvents());
    const calendarOptions = reactive<CalendarOptions>({
      timeZone: 'local',
      locale: thLocale,
      plugins: [
        dayGridPlugin,
        listPlugin,
        interactionPlugin,
      ],
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,listWeek'
      },
      initialView: 'dayGridMonth',
      firstDay: 0, // Sunday
      editable: false,
      selectable: false,
      selectMirror: true,
      dayMaxEvents: true,
      weekends: true,
      select: (selectInfo: DateSelectArg) => handleDateSelect(selectInfo),
      eventClick: (clickInfo: EventClickArg) => handleEventClick(clickInfo),
      eventDrop: (dropInfo: EventDropArg) => handleDropEvent(dropInfo),
      eventResize: (resizeInfo: EventResizeDoneArg) => handleReszieEvent(resizeInfo),
      eventDidMount: (mountInfo: EventMountArg) => handleMountEvent(mountInfo),
    });

    onMounted(() => {
      store.dispatch(AccountAction.GET)
        .then(() => store.dispatch(ActivityAction.FETCH))
        .then(() => store.dispatch(HolidayAction.FETCH))
        .then(() => {
          calendarOptions.editable = isOrs.value;
          calendarOptions.selectable = isOrs.value;
        });
    });

    const getEvents = () => {
      const holidayInput = holidays.value.map<EventInput>(m => ({
        id: `H-${m.id}`,
        title: m.name,
        start: m.date,
        end: (new Date(m.date)).setDate(m.date.getDate() + 1),
        allDay: true,
        display: 'background',
        classNames: ['fc-holiday'],
      }));

      const activityInput = activities.value.map<EventInput>(m => ({
        id: m.id.toString(),
        title: m.name,
        start: m.startDate,
        end: (new Date(m.endDate)).setDate(m.endDate.getDate() + 1),
        allDay: true,
      }));

      return holidayInput.concat(activityInput);
    };

    const handleDateSelect = (selectInfo: DateSelectArg) => {
      new Promise<void>((resolve) => {
        selectedActivityId.value = null;
        startDate.value = selectInfo.start;
        endDate.value = new Date(new Date(selectInfo.end).setDate(selectInfo.end.getDate() - 1)),
        resolve();
      })
        .then(() => tooltipList.value.map(element => element.hide()))
        .then(() => activityForm.value?.show());
    };

    const handleEventClick = (clickInfo: EventClickArg) => {
      if (!clickInfo.event.id.startsWith('H')) {
        Promise.resolve(selectedActivityId.value = parseInt(clickInfo.event.id))
          .then(() => tooltipList.value.map(element => element.hide()))
          .then(() => activityOffcanvas.value?.show());
      }
    };

    const handleDropEvent = (dropInfo: EventDropArg) => {
      const activity = activities.value.find(m => m.id == parseInt(dropInfo.event.id));
      if (activity) {
        const end = new Date(dropInfo.event.end as Date);
        store.dispatch(ActivityAction.UPDATE, {
          id: activity.id,
          name: activity.name,
          description: activity.description,
          startDate: dropInfo.event.start,
          endDate: new Date(end.setDate(end.getDate() - 1)),
          provinces: activity.provinces.map(m => m.name),
          hasMessage: activity.hasMessage,
        });
      }
    };

    const handleReszieEvent = (resizeInfo: EventResizeDoneArg) => {
      const activity = activities.value.find(m => m.id == parseInt(resizeInfo.event.id));
      if (activity) {
        const end = new Date(resizeInfo.event.end as Date);
        store.dispatch(ActivityAction.UPDATE, {
          id: activity.id,
          name: activity.name,
          description: activity.description,
          startDate: activity.startDate,
          endDate: new Date(end.setDate(end.getDate() - 1)),
          provinces: activity.provinces.map(m => m.name),
          hasMessage: activity.hasMessage,
        });
      }
    };

    const handleMountEvent = (mountInfo: EventMountArg) => {
      tooltipList.value.push(new Tooltip(mountInfo.el, {
        title: mountInfo.event.title,
        placement: 'top',
        trigger: 'hover',
        container: 'body'
      }));
    };

    const editClick = (id: number) => {
      new Promise<void>((resolve) => {
        selectedActivityId.value = id;
        startDate.value = null;
        endDate.value = null;
        resolve();
      }).then(() => activityForm.value?.show());
    };

    return {
      role,
      startDate,
      endDate,
      selectedActivityId,
      activityForm,
      activityOffcanvas,
      fullCalendarEvents,
      calendarOptions,
      editClick,
    };
  },
});
